import { useQuery } from "@tanstack/react-query";
import tradeService from "../services/trade.service";
import { Link, useParams } from "react-router-dom";
import ErrorPage from "./ErrorPage.component";

import Loader from "./Spinner.component";
import ScaleTable from "./uiElements/ScaleTable.component";
import { Accordion, Button, ListGroup, Table } from "react-bootstrap";
import ConfirmDelete from "./uiElements/ConfirmDelete.component";

import { useDeleteTrade } from "../hooks/useDeleteTrade";
import Modal from "./uiElements/Modal.component";
import AddTrade from "./AddTrade.component";
import Scaling from "./Scaling.component";
import ConfirmReset from "./uiElements/ConfirmReset.component";
import { useResetTrade } from "../hooks/useTrades";

function TradeDetails({ onCloseModal }) {
  const { id } = useParams();

  const {
    data: trade,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["trade"],
    queryFn: () => tradeService.getTradeById(id),
  });

  const { isDeleting, deleteTrade } = useDeleteTrade();

  const { isReseting, resetTrade } = useResetTrade();

  const onReset = () => {
    resetTrade(trade._id);
  };

  if (isLoading) return <Loader />;

  if (error) return <ErrorPage errorCode={500} description={"Something Went Wrong"} />;

  const { _id, ticker, profit, tradeStatus, tradeDate, scaleIns, scaleOuts } = trade;

  return (
    <>
      <div>
        <h3 className="mb-4">TRADE DETAILS - {ticker} - </h3>

        <hr />
        <div className="mb-3">
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <div>
                  {ticker} - {tradeDate.split("T")[0]}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="d-flex justify-content-center gap-2 mb-4">
                  <Modal>
                    <Modal.Open opens="Delete-Trade">
                      <Button variant="danger">DELETE</Button>
                    </Modal.Open>
                    <Modal.Window name="Delete-Trade">
                      <ConfirmDelete
                        resourceName={trade.ticker}
                        onConfirm={() => deleteTrade(trade._id)}
                        disabled={isDeleting}
                      />
                    </Modal.Window>
                  </Modal>
                  <Modal>
                    <Modal.Open opens="scale-in">
                      <Button variant="success">SCALE IN</Button>
                    </Modal.Open>
                    <Modal.Window name="scale-in">
                      <Scaling tradeId={trade._id} ticker={trade.ticker} type="Scale In" />
                    </Modal.Window>
                  </Modal>
                  <Modal>
                    <Modal.Open opens="scale-out">
                      <Button disabled={trade.quantity === 0} variant="primary">
                        SCALE OUT
                      </Button>
                    </Modal.Open>
                    <Modal.Window name="scale-out">
                      <Scaling type="Scale Out" tradeId={trade._id} ticker={trade.ticker} />
                    </Modal.Window>
                  </Modal>
                  <Modal>
                    <Modal.Open opens="Edit-Trade">
                      <Button variant="secondary">EDIT </Button>
                    </Modal.Open>
                    <Modal.Window name="Edit-Trade">
                      <AddTrade tradeToEdit={trade} />
                    </Modal.Window>
                  </Modal>
                  <Modal>
                    <Modal.Open opens="Edit-Trade">
                      <Button variant="success">RESET </Button>
                    </Modal.Open>
                    <Modal.Window name="Edit-Trade">
                      <ConfirmReset resourceName="Trade" disabled={isReseting} onConfirm={onReset} />
                    </Modal.Window>
                  </Modal>
                </div>
                <div className="d-flex gap-4 ">
                  <ListGroup className="w-50">
                    <ListGroup.Item className="fw-bold d-flex gap-4 p-3">
                      <div className="d-flex justify-content-between  w-100">
                        <span>Initial Trade Entries</span>
                        <span>{trade.tradeDate.split("T")[0]}</span>
                      </div>
                    </ListGroup.Item>
                    <Table hover bordered striped>
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Ticker:</td>
                          <td className="fw-bold">
                            <Link to={`/trade-journal/trade-ticker/${trade.ticker}`}>{trade.ticker}</Link>
                          </td>
                        </tr>
                        <tr>
                          <td>Entry Price:</td>
                          <td>{trade.entryPrice}</td>
                        </tr>
                        <tr>
                          <td>Quantity:</td>
                          <td>{trade.quantity}</td>
                        </tr>
                        <tr>
                          <td>Trade Type:</td>
                          <td>{trade.tradeType}</td>
                        </tr>
                        <tr>
                          <td>Take profit:</td>
                          <td>{trade.takeProfit}</td>
                        </tr>
                        <tr>
                          <td>Stop Loss:</td>
                          <td>{trade.stopLoss}</td>
                        </tr>
                        <tr>
                          <td>Trade rationale:</td>
                          <td>{trade.tradeRationale}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </ListGroup>

                  <ListGroup className="w-50">
                    <ListGroup.Item className="fw-bold d-flex gap-4 p-3">
                      <div className="d-flex justify-content-between  w-100">
                        <div>
                          Trade Profit/Loss :
                          <span className={profit < 0 ? "text-danger" : "text-success"}>
                            {" "}
                            {parseInt(profit.toFixed(2))}{" "}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className={`badge ${tradeStatus === "open" ? "bg-success" : "bg-danger"} rounded-pill`}>
                          {tradeStatus}
                        </span>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>Remaing Quantity : {trade.remainQuantity}</ListGroup.Item>
                    <ListGroup.Item>Average price : {trade.averagePrice}</ListGroup.Item>
                    <ListGroup.Item className="fw-bold">Trade Summary</ListGroup.Item>
                    <ListGroup.Item>
                      <span>Entries</span>
                      <Table>
                        <thead>
                          <tr>
                            <th>price</th>
                            <th>quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{trade.entryPrice}</td>
                            <td>{trade.quantity}</td>
                            <td>{trade.tradeCost}</td>
                          </tr>
                          {trade.scaleIns.map((scale, i) => (
                            <tr key={i}>
                              <td>{scale.price}</td>
                              <td>{scale.quantity}</td>
                              <td>{scale.scaleCost}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <span>Exits</span>
                      <Table>
                        <thead>
                          <tr>
                            <th>price</th>
                            <th>quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trade.scaleOuts.map((scale, i) => (
                            <tr key={i}>
                              <td>{scale.price}</td>
                              <td>{scale.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </ListGroup.Item>
                  </ListGroup>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <ScaleTable type="in" scales={scaleIns} ticker={ticker} tradeId={_id} />
        <ScaleTable type="out" scales={scaleOuts} ticker={ticker} tradeId={_id} />
      </div>
    </>
  );
}

export default TradeDetails;
